import { useState, useEffect, useRef } from "react";
import { signIn, signUp } from "./lib/auth-client";
import "./Auth.scss";
import toast from "react-hot-toast";

export const Auth = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [isSignIn, setIsSignIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (containerRef.current?.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (isSignIn) {
        const res = await signIn.email({ email, password });
        if (res.data?.user) {
          toast.success("Signed in successfully");
          onSuccess();
        }

        if (res.error) {
          toast.error(
            res.error.message ??
              "Not able to sign in, Please try again or sign up",
          );
        }
      } else {
        const res = await signUp.email({ email, password, name });
        if (res.data?.user) {
          toast.success("Signed up successfully");
          onSuccess();
        }

        if (res.error) {
          toast.error(
            res.error.message ??
              "Not able to sign up, Please try again or sign in",
          );
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container" ref={containerRef}>
      <div className="auth-content">
        <div className="auth-header">
          <h2>
            {isSignIn ? "Sign in to your account" : "Create your account"}
          </h2>
          <p>
            {isSignIn ? (
              <>
                Or{" "}
                <button
                  onClick={() => setIsSignIn(false)}
                  className="auth-toggle"
                >
                  create a new account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => setIsSignIn(true)}
                  className="auth-toggle"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="input-group">
            {!isSignIn && (
              <div className="form-group">
                <label htmlFor="name" className="sr-only">
                  Full Name
                </label>
                <input
                  id="name"
                  autoFocus
                  name="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full Name"
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                autoFocus
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading && (
              <svg
                className="loading-spinner"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isSignIn
              ? isLoading
                ? "Signing in..."
                : "Sign in"
              : isLoading
              ? "Creating account..."
              : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Auth;
